import React, { ReactNode, useEffect, useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';

import { ChevronIcon } from '../../assets/icons';
import Button from '../Button';
import Dropdown, { DropdownItem } from '../Dropdown';

interface Props<TOption> {
    options: TOption[];
    optionKey?: TOption extends undefined ? undefined : keyof TOption;
    isOpen?: boolean;
    className?: string;
    initialValue?: TOption | TOption[keyof TOption];
    onChange?: (option: TOption, optionKey?: keyof TOption) => void;
    onClick?: (e: React.MouseEvent<HTMLButtonElement>, selectedOption: TOption) => void;
    renderOption?: (option: TOption) => ReactNode;
    renderSelected?: (selectedOption: TOption) => ReactNode;
    setIsOpen?: (open: boolean) => void;
}

const Select = <TOption,>({
    options,
    optionKey,
    isOpen,
    className = '',
    initialValue,
    onChange,
    onClick,
    renderOption,
    renderSelected,
    setIsOpen,
}: Props<TOption>) => {
    const [active, setActive] = useState(() => {
        const defaultOption = options[0];
        if (initialValue) {
            const option = options.find(
                (opt) => (optionKey ? opt[optionKey] : opt) === initialValue
            );
            return option || defaultOption;
        }
        return defaultOption;
    });
    const [open, setOpen] = useState(false);

    // If the initialValue has arrived after first render, use it as active
    // This is to allow initialValue to be provided by an async callback
    useEffect(() => {
        if (!!initialValue) {
            const option = options.find(
                (opt) => (optionKey ? opt[optionKey] : opt) === initialValue
            );
            if (option) {
                setActive(option);
            }
        }
    }, [initialValue, optionKey, options]);

    const toggleDropdown = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (onClick) {
            onClick(e, active);
        }

        if (setIsOpen) {
            setIsOpen(!isOpen);
            return;
        }

        setOpen((prev) => !prev);
    };

    const closeDropdown = () => {
        if (setIsOpen && isOpen) {
            setIsOpen(false);
            return;
        }

        if (open) {
            setOpen(false);
        }
    };

    const handleOptionChange = (option: TOption) => {
        onChange && onChange(option, optionKey);
        setActive(option);
        closeDropdown();
    };

    return (
        <ClickAwayListener onClickAway={closeDropdown}>
            <div className={`select-custom ${className}`}>
                <Button className='selected-label' onClick={toggleDropdown}>
                    {renderSelected
                        ? renderSelected(active)
                        : `${optionKey ? active[optionKey] : active}`}
                </Button>
                <Dropdown open={isOpen ?? open}>
                    {options.map((option, index) => (
                        <DropdownItem
                            key={index}
                            active={
                                // option could be undefined. since TOption is a generic
                                optionKey && option && active
                                    ? active[optionKey] === option[optionKey]
                                    : active === option
                            }
                            onClick={() => handleOptionChange(option)}
                        >
                            {renderOption
                                ? renderOption(option)
                                : `${optionKey ? option[optionKey] : option}`}
                        </DropdownItem>
                    ))}
                </Dropdown>
                <ChevronIcon />
            </div>
        </ClickAwayListener>
    );
};

export default Select;
